/**
 * Preset marketing copy for the member Promote page.
 *
 * Each template's ``body`` is a function that receives the user's full
 * referral link (already prefixed with https:// + custom domain when
 * available) and returns the rendered, ready-to-copy text.
 *
 * Tone guide:
 *   - cold market: intrigue-led, no MLM jargon, conversational. The
 *     reader does NOT know what 1SimpleCycler is yet.
 *   - warm market: assumes prior context, friend-to-friend casual.
 *   - platform-specific posts are written FOR that platform's audience
 *     and ranking dynamics (Reddit needs a TL;DR + disclosure;
 *     LinkedIn stays professional; Instagram leans visual + emojis).
 *
 * Adding a new template? Append to the array — the PromoSubpage renders
 * the grid dynamically from this list.
 */

const TEMPLATES = [
  // -------- SMS / Text Message --------
  {
    id: 'sms_cold',
    channel: 'Text Message',
    audience: 'Cold Market',
    icon: 'MessageSquare',
    body: (refLink) =>
      `Hey — quick question. Are you open to earning a few hundred extra in crypto a month if it only takes about 5 min to set up? No fluff, no pitch deck. Take a look when you can: ${refLink}`,
  },
  {
    id: 'sms_warm',
    channel: 'Text Message',
    audience: 'Warm Market',
    icon: 'MessageSquare',
    body: (refLink) =>
      `Bro/sis — I had to text you. Found a crypto cycler that's actually paying out and I'm already in profit. No subscription, no recruiting required, just real positions cycling. Take 2 min and look: ${refLink}`,
  },

  // -------- Email --------
  {
    id: 'email_cold',
    channel: 'Email',
    audience: 'Cold Market',
    icon: 'Mail',
    body: (refLink) =>
      `Subject: Quick crypto question — would you be open?\n\nHi there,\n\nI wanted to send this directly instead of posting publicly. I've been using a crypto micro-cycler platform called 1SimpleCycler that's been paying out consistently. It's not an investment scheme, it's not a course, and there are no monthly fees. You open a position once, the matrix cycles, you withdraw.\n\nNo pressure — I just thought you might want to see how it works. If it's not for you, no worries at all.\n\nLink: ${refLink}\n\nTalk soon`,
  },
  {
    id: 'email_warm',
    channel: 'Email',
    audience: 'Warm Market',
    icon: 'Mail',
    body: (refLink) =>
      `Subject: Had to tell you about this\n\nHey,\n\nI know I usually don't share stuff like this, but I've been on 1SimpleCycler for a bit now and the withdrawals are real. I'm in profit and the positions keep cycling. Thought of you because I think you'd actually use this the right way.\n\nThere's no upline pressure, no subscription, no calls. You buy a position, it cycles, you withdraw. That's it.\n\nLook when you have 5 min: ${refLink}\n\nIf you want me to walk you through it, just say the word.`,
  },

  // -------- Facebook --------
  {
    id: 'facebook',
    channel: 'Facebook',
    audience: 'Public Post',
    icon: 'Facebook',
    body: (refLink) =>
      `Real crypto. Real payouts. No subscription. No recruiting required.\n\n1SimpleCycler runs a 3×1 matrix that cycles positions automatically. You open a position, the math does the work, you withdraw to your own wallet. I'm in profit and it took me 5 minutes to set up.\n\nIf you've been burned by hype before, give this one a fair look. ⬇️\n\n👉 ${refLink}`,
  },

  // -------- Instagram --------
  {
    id: 'instagram',
    channel: 'Instagram',
    audience: 'Caption',
    icon: 'Instagram',
    body: (refLink) =>
      `Just got paid 💸\n\nNo cap — 1SimpleCycler is the real deal. 3×1 matrix, real crypto, real withdrawals. Open a position once, the cycle runs itself.\n\nLink in bio + here:\n${refLink}\n\n#crypto #cryptopayouts #passiveincome #1simplecycler #ltc #trx #doge`,
  },

  // -------- Reddit --------
  {
    id: 'reddit',
    channel: 'Reddit',
    audience: 'Subreddit Post',
    icon: 'MessageCircle',
    body: (refLink) =>
      `**TL;DR:** Found a 3×1 micro-cycler that pays out in LTC/TRX/DOGE. No subscription. Open a position once, it cycles, you withdraw to your own wallet.\n\nFull disclosure: link below is my referral. Use it or don't — the platform works the same either way.\n\nHow it works:\n- Buy a position with crypto (small amount)\n- Matrix cycles automatically when 3 other positions fill under yours\n- Cycle proceeds hit your withdrawal balance\n- Withdraw to your own wallet (NOWPayments, on-chain)\n\nNot financial advice. Do your own DD. I'm just sharing what's working for me.\n\n${refLink}`,
  },

  // -------- LinkedIn --------
  {
    id: 'linkedin',
    channel: 'LinkedIn',
    audience: 'Professional Post',
    icon: 'Linkedin',
    body: (refLink) =>
      `I don't usually post about crypto opportunities on LinkedIn, but this one is worth a closer look.\n\n1SimpleCycler is a transparent 3×1 matrix platform with on-chain settlement. Positions cycle deterministically; payouts go to user-controlled wallets via NOWPayments. No recurring fees, no subscription model, no obligation to refer.\n\nFor anyone interested in low-friction crypto income mechanics, it's worth 5 minutes of due diligence.\n\nLink: ${refLink}`,
  },
];

export default TEMPLATES;
